import { omit } from "lodash-es";
import { BaseSupabaseService } from "../../../server/supabase/base-supabase-service";

import type { Database } from "../../../__definitions__/database-definitions";
import type { ISO8601_DATE } from "../../../utils/iso8601";
import type { Library, LibraryProgram, LibraryTemplate } from "../library/types";
import type { PostgrestError } from "@supabase/postgrest-js";

export type AtlasDiscoverRow = Database["atlas"]["Views"]["discover_templates"]["Row"];

const constructLibraryTemplate = (
  template: NonNullable<Database["atlas"]["Tables"]["discover"]["Row"]["metadata"]>["template"],
  program: Omit<LibraryProgram, "templates">,
): LibraryTemplate => ({
  type: "moment",
  id: template.airtable.id,
  slug: template.data.data.slug,
  title: template.data.data.title,
  what: template.data.data.what,
  context: template.data.data.context,
  img: {
    ...omit(template.data.data.img, "id"),
    template_code: template.data.data.img.id,
  },
  content: template.data.data.content,
  recommendations: template.data.data.recommendations,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  date: template.data.data.date ? (template.data.data.date as ISO8601_DATE) : null,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  end_date: template.data.data.end_date ? (template.data.data.end_date as ISO8601_DATE) : null,
  trigger: template.data.data.trigger,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  segment: template.data.data.segment as LibraryTemplate["segment"],
  trending: template.data.data.trending ?? null,
  hero: template.data.data.hero ?? null,
  top: template.data.data.top ?? null,
  geo: template.data.data.geo ?? null,
  program,
});

const constructLibraryProgram = (
  program: NonNullable<Database["atlas"]["Tables"]["discover"]["Row"]["metadata"]>["program"],
): LibraryProgram => ({
  slug: program.data.slug,
  name: program.data.name,
  photo: program.data.photo ?? "",
  color: program.data.color ?? "",
  templates: [],
});

export class SupabaseAtlasService extends BaseSupabaseService<"atlas"> {
  table = "discover_templates" as const;

  static rowsToLibrary(rows: AtlasDiscoverRow[]): Library {
    // group by program

    const programs = rows.reduce<Record<string, LibraryProgram>>((acc, row) => {
      if (!row.metadata) return acc;
      const { program, template } = row.metadata;

      if (!acc[program.data.slug]) {
        acc[program.data.slug] = constructLibraryProgram(program);
      }

      acc[program.data.slug]!.templates.push(constructLibraryTemplate(template, program.data));
      return acc;
    }, {});

    return { programs: Object.values(programs) };
  }

  async getAtlasV2Library(): Promise<
    { data: Library; error: null } | { data: null; error: PostgrestError }
  > {
    let query = this.client.from(this.table).select("*");
    query = query.filter("sequence_template", "is", false);
    const { data, error } = await this.getAllFromSelection(null, query, {
      order: [{ column: "template_id" }],
    });

    if (error) return { data, error };

    return { data: SupabaseAtlasService.rowsToLibrary(data), error: null };
  }
}
